import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, Loader, AlertCircle } from 'lucide-react'
import { WaveIcon } from '@/components/common/WaveIcon'

// Public-facing application page — no auth required
// Route: /apply/:projectId

interface FormConfig {
  roleTitle: string
  company: string
  isOpen: boolean
}

const API_BASE = (import.meta.env.VITE_ENGINE_API_BASE_URL ?? '').replace(/\/$/, '')

export const ApplyPage = () => {
  const { projectId } = useParams<{ projectId: string }>()

  const [config, setConfig] = useState<FormConfig | null>(null)
  const [configError, setConfigError] = useState(false)

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    currentTitle: '',
    currentCompany: '',
    linkedinUrl: '',
    coverNote: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!projectId) return
    fetch(`${API_BASE}/api/engine/projects/${projectId}/apply`)
      .then(r => r.json())
      .then((data: FormConfig) => setConfig(data))
      .catch(() => setConfigError(true))
  }, [projectId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!projectId) return
    setIsSubmitting(true)
    setError(null)

    try {
      const res = await fetch(`${API_BASE}/api/engine/projects/${projectId}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'Submission failed')
      setSubmitMessage(data.message)
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const update = (field: keyof typeof form, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }))

  if (configError) {
    return (
      <div className="min-h-screen bg-[#02182B] flex items-center justify-center px-4">
        <div className="text-center space-y-3">
          <AlertCircle size={32} className="text-red-400 mx-auto" />
          <p className="text-white font-semibold">Role not found</p>
          <p className="text-gray-400 text-sm">This application link may have expired or the role may have been filled.</p>
        </div>
      </div>
    )
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-[#02182B] flex items-center justify-center">
        <Loader size={24} className="animate-spin text-[#FD802E]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#02182B]">
      {/* Header */}
      <div className="border-b border-[#233D4C] bg-[#02182B]">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="w-8 h-8 bg-[#FD802E] rounded-lg flex items-center justify-center">
            <WaveIcon size="sm" />
          </div>
          <span className="text-white font-bold text-lg">Searchline</span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {submitted ? (
          <div className="bg-[#233D4C] border border-green-500/30 rounded-xl p-10 text-center space-y-4">
            <CheckCircle size={40} className="text-green-400 mx-auto" />
            <h2 className="text-white text-xl font-semibold">Application received</h2>
            <p className="text-gray-300 text-sm leading-relaxed max-w-md mx-auto">{submitMessage}</p>
            <p className="text-gray-500 text-xs">Powered by Searchline AI</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Role header */}
            <div>
              <h1 className="text-3xl font-bold text-white">{config.roleTitle}</h1>
              {config.company && (
                <p className="text-gray-400 text-lg mt-1">{config.company}</p>
              )}
              <p className="text-gray-500 text-sm mt-3">
                Fill in the form below and our AI will review your application and match you against the role criteria.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={form.fullName}
                    onChange={e => update('fullName', e.target.value)}
                    placeholder="Jane Smith"
                    className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1.5">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => update('email', e.target.value)}
                    placeholder="jane@company.com"
                    className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] transition-colors"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1.5">Current Title *</label>
                  <input
                    type="text"
                    required
                    value={form.currentTitle}
                    onChange={e => update('currentTitle', e.target.value)}
                    placeholder="VP Sales"
                    className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-1.5">Current Company *</label>
                  <input
                    type="text"
                    required
                    value={form.currentCompany}
                    onChange={e => update('currentCompany', e.target.value)}
                    placeholder="Acme Corp"
                    className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1.5">LinkedIn Profile <span className="text-gray-500 font-normal">(optional)</span></label>
                <input
                  type="url"
                  value={form.linkedinUrl}
                  onChange={e => update('linkedinUrl', e.target.value)}
                  placeholder="https://linkedin.com/in/janesmith"
                  className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] transition-colors"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1.5">
                  Why are you a strong fit for this role? <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <textarea
                  rows={4}
                  value={form.coverNote}
                  onChange={e => update('coverNote', e.target.value)}
                  placeholder="Share the most relevant experience, achievements, or context you'd like us to consider…"
                  className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-[#FD802E] transition-colors resize-none"
                />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FD802E] text-[#233D4C] font-bold py-4 rounded-xl hover:bg-[#ff8f45] transition-colors disabled:opacity-50 text-base"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader size={18} className="animate-spin" /> Submitting…
                  </span>
                ) : (
                  'Submit Application'
                )}
              </button>

              <p className="text-gray-600 text-xs text-center">
                Your application is reviewed by AI against the role criteria. We never share your details without your consent.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
